<template>
  <div v-if='pageReady' id='pdf-ready'>
    <ExportCover preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()' />
    <div class='print-page print-portrait x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Filters Applied</h2>
      </ExportPageHeading>
      <ul class='mt-[5mm] text-sm divide-y border-t border-b'>
        <li class='py-1 text-xs'>
          Year<br />{{ state.filters.year.to }}
        </li>
        <li class='py-1 text-xs'>
          Compare To<br />{{ state.filters.year.from }}
        </li>
      </ul>
    </div>
    <div class='print-page print-landscape x-auto-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Results</h2>
        <p>Scores for all engagements.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualPortfolioTable
          :params='params'
          :showOrganisation='isSuperAdminView()'
          :showAssetManager='true'
          :view='view'
          :isPrint='true'
        />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Theme Performance</h2>
        <p>The distribution of engagement scores by theme for your organisation.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualThemePie :params='params' :showKey='true' />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Asset Performance</h2>
        <p>The distribution of engagement scores by asset for your organisation.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualEntityBar :params='params' :dimension='`${view}Asset`' :showKey='true' />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Asset Managers Performance</h2>
        <p>The distribution of engagement scores by asset manager for your organisation.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualEntityBar :params='params' :dimension='`${view}AssetManager`' :showKey='true' />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Change</h2>
        <p>Percentages of engagements that had a positive shift.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualChangeBar :params='params' />
      </div>
    </div>
    <ExportDisclaimerPage preTitle='Annual Portfolio Report' :date='new Date()' :title='getExportOrganisation()' />
  </div>
</template>

<script setup>

import { computed, reactive, ref } from 'vue';

import { usePayloadRequest } from '@/kernel/api';
import { getStateFromRoute } from '@/router';
import { rehydrateCollections, getExportTitle, getExportOrganisation } from '@/kernel/report';

import ExportCover from '@/components/export/export-cover.vue';
import ExportPageHeading from '@/components/export/export-page-heading.vue';
import ExportDisclaimerPage from '@/components/export/export-disclaimer-page.vue';

import GraphAnnualPortfolioTable from '@/components/reports/graphs/graph-annual-portfolio-table.vue';
import GraphAnnualThemePie from '@/components/reports/graphs/graph-annual-theme-pie.vue';
import GraphAnnualEntityBar from '@/components/reports/graphs/graph-annual-entity-bar.vue';
import GraphAnnualChangeBar from '@/components/reports/graphs/graph-annual-change-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

const organisationId = computed(() => {
  return getOrganisationId();
});

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const filterCollectionMap = [
  {key: 'assessmentTemplate', collection: 'assessment-template'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 1,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      assessmentTemplate: preload?.filters?.assessmentTemplate || null
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const params = ref(null);

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.from === state.filters.year.to) {
    return null;
  }

  const args = {
    resolution: 'year',
    year: state.filters.year
  }

  if (state.filters.assessmentTemplate) {
    args.assessmentTemplate = state.filters.assessmentTemplate.id;
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
}

const loadState = async () => {
  document.title = getExportTitle(`Annual Portfolio Report - ${getExportOrganisation()}`, new Date());

  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
  }

  params.value = calcParams();

  pageReady.value = true;
}

loadState();

</script>
